import type { ReactNode } from 'react';

import { MarketingSiteLayout } from '../components/marketing/MarketingSiteLayout';
import './aboutPage.css';

const ABOUT_CONTACT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined)?.trim() || 'support@kynward.com';

const BELIEF_VALUES = [
  'Focused spaces instead of endless feeds',
  'Conversation before pressure',
  'Mutual interest instead of cold access',
  'Safety and moderation from the beginning',
  'Local connection that feels grounded in real life',
  'Product decisions that respect people’s time, comfort, and boundaries',
] as const;

function AboutSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="about-section" aria-labelledby={id}>
      <h2 id={id} className="about-h2">
        {title}
      </h2>
      <div className="about-section__body">{children}</div>
    </section>
  );
}

function AboutParagraphs({ paragraphs }: { paragraphs: readonly string[] }) {
  return (
    <>
      {paragraphs.map((text) => (
        <p key={text} className="about-p">
          {text}
        </p>
      ))}
    </>
  );
}

export function AboutPage() {
  return (
    <MarketingSiteLayout>
      <article className="about-page">
        <header className="about-hero">
          <p className="about-kicker">Elyxora Works</p>
          <h1 className="about-h1">About Elyxora Works</h1>
          <p className="about-lead">
            Elyxora Works is an independent product studio building calmer, more intentional
            technology for the way people connect.
          </p>
          <AboutParagraphs
            paragraphs={[
              'The company was created around a simple belief: software should help people feel more grounded, not more overwhelmed. Too many social products are built around speed, feeds, swipes, noise, and constant performance. Elyxora Works is interested in a different kind of product — one that creates room for trust, context, and real human pacing.',
              'Kynward is the first product from Elyxora Works.',
            ]}
          />
        </header>

        <div className="about-content">
          <AboutSection
            id="about-why"
            title="Why we're building"
            children={
              <AboutParagraphs
                paragraphs={[
                  'Meeting people should not feel like a performance.',
                  'It should not require endless swiping, cold messages, or trying to summarize yourself into a perfect profile. It should not feel like another feed competing for attention.',
                  'Elyxora Works is building products that start from a different place: focused spaces, clearer boundaries, and more thoughtful ways for people to interact.',
                  'Kynward began with that idea.',
                  'Instead of pushing people into instant judgment or one-on-one pressure, Kynward creates local Circles where conversation has a place to begin. The goal is not to maximize attention. The goal is to make connection feel more natural, safer, and easier to continue when it is mutual.',
                ]}
              />
            }
          />

          <AboutSection
            id="about-believe"
            title="What we believe"
            children={
              <>
                <p className="about-p">
                  We believe social technology should be more human than addictive.
                </p>
                <p className="about-p">That means building around:</p>
                <ul className="about-card-grid">
                  {BELIEF_VALUES.map((label) => (
                    <li key={label} className="about-mini-card">
                      {label}
                    </li>
                  ))}
                </ul>
                <p className="about-p about-p--after-cards">
                  These ideas shape Kynward, and they will shape future products from Elyxora Works.
                </p>
              </>
            }
          />

          <AboutSection
            id="about-kynward"
            title="Starting with Kynward"
            children={
              <AboutParagraphs
                paragraphs={[
                  'Kynward is the first step.',
                  'It is a social app for meeting people through group conversations called Circles. The product is starting carefully, with a focus on quality, safety, and feedback instead of trying to scale before the experience feels right.',
                  'The goal is to build something people can actually trust: a place where meeting new people feels less awkward, less performative, and less disposable.',
                ]}
              />
            }
          />

          <AboutSection
            id="about-name"
            title="Why 'Kynward'"
            children={
              <AboutParagraphs
                paragraphs={[
                  '"Kynward" is a small piece of wordplay — kin, and onward.',
                  'The name is meant as a promise: the app should move people toward the people who feel like their kin, whether that becomes friendship, dating, or something in between.',
                ]}
              />
            }
          />

          <AboutSection
            id="about-independent"
            title="Built independently"
            children={
              <AboutParagraphs
                paragraphs={[
                  'Elyxora Works is independently built and intentionally focused.',
                  'That means the company can move carefully, listen closely, and make product decisions around the experience instead of chasing noise for its own sake.',
                  'The work is early, but the direction is clear: build technology that helps people connect with more context, more care, and less pressure.',
                ]}
              />
            }
          />

          <section className="about-section about-section--contact" aria-labelledby="about-contact">
            <h2 id="about-contact" className="about-h2">
              Contact
            </h2>
            <div className="about-card about-card--contact">
              <p className="about-p">
                For questions, feedback, partnerships, or safety concerns, contact:
              </p>
              <p className="about-p">
                <a className="about-a" href={`mailto:${ABOUT_CONTACT_EMAIL}`}>
                  {ABOUT_CONTACT_EMAIL}
                </a>
              </p>
            </div>
          </section>
        </div>
      </article>
    </MarketingSiteLayout>
  );
}
